import React, { useState } from "react";
import PropTypes from "prop-types";
import { useFormContext, Controller } from "react-hook-form";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  Container,
  Row,
  Col,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import EditorFormSection from "../../EditorFormSection";
import { FILEEDITORTYPES, FILEEDITOR } from "../utils/fileEditor";
import { FaChevronDown } from "react-icons/fa";
import { Link } from "react-router-dom";
import { isServer } from "../../../redux/store";
import DataLayerHelper from "../../../utils/dataLayer";

const ProductSelectionSection = (
  { isOpen, togglePanel, language },
  context
) => {
  const { control, setValue, watch } = useFormContext();
  const [isModalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("vinyls");
  const smSize = 6;
  const smallDevice = !isServer
    ? window.matchMedia("(max-width: 991px)").matches
    : false;

  const toggleModal = () => setModalOpen(!isModalOpen);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const availableEditorTypes = Object.fromEntries(
    Object.keys(FILEEDITORTYPES).map((editorTypeKey) => [
      FILEEDITORTYPES[editorTypeKey],
      {
        key: FILEEDITORTYPES[editorTypeKey],
        name: editorTypeKey,
        title: context.t(`${editorTypeKey}.title`),
        description: context.t(`${editorTypeKey}.description`),
        img: FILEEDITOR[FILEEDITORTYPES[editorTypeKey]]?.icon || "",
      },
    ])
  );
  const editorTypeId = watch("editorType");
  const selectedEditorType = availableEditorTypes[editorTypeId];

  const placeholder = "https://cdn.myfolie.de/media/20x20.png";

  const renderModalCard = (name) => {
    const key = FILEEDITORTYPES[name];
    const editorTypeOptions = availableEditorTypes[key];
    return (
      <Row
        className="editortypefield__option py-2 h-100"
        key={editorTypeOptions.name}
        onClick={() => {
          setValue("editorType", editorTypeOptions.key);
          toggleModal();
          DataLayerHelper.addEventWithOrigin(
            "behaviour",
            "file editor",
            "EditorTypeSelection",
            editorTypeOptions.name
          );
        }}
      >
        <Col xs={3}>
          <img
            src={editorTypeOptions.img || placeholder}
            alt="selected-editor-img"
            className="img-fluid"
            loading="lazy"
          />
        </Col>
        <Col xs={9}>
          <p className="h6">{context.t(`${name}.title`)}</p>
          <p className="semi-small">{context.t(`${name}.description`)}</p>
        </Col>
      </Row>
    );
  };

  return (
    <EditorFormSection
      panelName="productselection"
      panelTitle={context.t("What product should we print for you?")}
      open={isOpen}
      keepOpen
      toggle={togglePanel}
    >
      <Controller
        name="editorType"
        control={control}
        render={({ field }) => (
          <>
            <div className="editortype-field-value">
              <Container onClick={toggleModal}>
                <Row key={selectedEditorType?.name + "_selected"}>
                  <Col xs={4}>
                    <img
                      src={selectedEditorType?.img || placeholder}
                      alt="selected-editor-img"
                      className="img-fluid my-3"
                      loading="lazy"
                    />
                  </Col>
                  <Col xs={8}>
                    <div className="text-right text-primary">
                      <span className="btn btn-link btn-sm">
                        {context.t("change")} <FaChevronDown />
                      </span>
                    </div>
                    <p className="font-weight-bold">
                      {context.t(selectedEditorType?.name + ".title")}
                    </p>
                  </Col>
                </Row>
              </Container>
            </div>

            <Modal
              fade={false}
              size="xl"
              isOpen={isModalOpen}
              toggle={toggleModal}
            >
              <ModalHeader toggle={toggleModal}>
                {context.t("Our products and materials")}
              </ModalHeader>
              <ModalBody className="productselectionmodal__body">
                <Nav
                  tabs
                  pills={smallDevice}
                  className="productselectionmodal__tabs mb-3"
                >
                  {[
                    "vinyls",
                    "stickers",
                    "floorprints",
                    "banners",
                    "foto_prints",
                    "signs",
                    "cutting_plots",
                  ].map((tab) => (
                    <NavItem key={tab}>
                      <NavLink
                        className={classnames(
                          { active: activeTab === tab },
                          "nav-link"
                        )}
                        onClick={() => toggleTab(tab)}
                      >
                        {context.t(tab)}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="vinyls">
                    <Row>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "STICKER",
                          context.t("STICKER.title"),
                          context.t("STICKER.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "STICKER_PREMIUM",
                          context.t("STICKER_PREMIUM.title"),
                          context.t("STICKER_PREMIUM.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "STICKER_WHITEPRINT",
                          context.t("STICKER_WHITEPRINT.title"),
                          context.t("STICKER_WHITEPRINT.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "STICKER_PVC_FREE",
                          context.t("STICKER_PVC_FREE.title"),
                          context.t("STICKER_PVC_FREE.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "STICKER_DOTTED",
                          context.t("STICKER_DOTTED.title"),
                          context.t("STICKER_DOTTED.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "STICKER_STRONG",
                          context.t("STICKER_STRONG.title"),
                          context.t("STICKER_STRONG.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "FROSTED",
                          context.t("FROSTED.title"),
                          context.t("FROSTED.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "ADHESION",
                          context.t("ADHESION.title"),
                          context.t("ADHESION.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "MAGNETIC",
                          context.t("MAGNETIC.title"),
                          context.t("MAGNETIC.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "PERFORATED",
                          context.t("PERFORATED.title"),
                          context.t("PERFORATED.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "STICKER_CARAVAN",
                          context.t("STICKER_CARAVAN.title"),
                          context.t("STICKER_CARAVAN.description")
                        )}
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="stickers">
                    <p className="semi-small">
                      {context.t(
                        "Use stickers to promote your business, brand or event! Your design will be printed on vinyl and optional protected with a laminate layer that to against scratches, water & sunlight. Minimum amount starts at 50, we recommend maximum size of 15cm x 15cm."
                      )}
                    </p>
                    <Row>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "STICKER_CIRCLE",
                          context.t("STICKER_CIRCLE.title"),
                          context.t("STICKER_CIRCLE.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "STICKER_RECTANGULAR",
                          context.t("STICKER_RECTANGULAR.title"),
                          context.t("STICKER_RECTANGULAR.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "STICKER_CUT",
                          context.t("STICKER_CUT.title"),
                          context.t("STICKER_CUT.description")
                        )}
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="floorprints">
                    <Row>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "FLOORPRINT",
                          context.t("FLOORPRINT.title"),
                          context.t("FLOORPRINT.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "FLOORPRINT_ECO",
                          context.t("FLOORPRINT_ECO.title"),
                          context.t("FLOORPRINT_ECO.description")
                        )}
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="banners">
                    <Row>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "BANNERPVC",
                          context.t("BANNERPVC.title"),
                          context.t("BANNERPVC.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "BANNERMESH",
                          context.t("BANNERMESH.title"),
                          context.t("BANNERMESH.description")
                        )}
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="foto_prints">
                    <Row>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "POSTER",
                          context.t("POSTER.title"),
                          context.t("POSTER.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "CANVAS",
                          context.t("CANVAS.title"),
                          context.t("CANVAS.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "PHOTOWALLPAPER",
                          context.t("PHOTOWALLPAPER.title"),
                          context.t("PHOTOWALLPAPER.description")
                        )}
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="signs">
                    <Row>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "PVCSIGN",
                          context.t("PVCSIGN.title"),
                          context.t("PVCSIGN.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "ALUSIGN",
                          context.t("ALUSIGN.title"),
                          context.t("ALUSIGN.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "MAGNETIC",
                          context.t("MAGNETIC.title"),
                          context.t("MAGNETIC.description")
                        )}
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="cutting_plots">
                    <p className="semi-small">
                      {context.t(
                        "Cutting plotts are technicaly not printed products but maybe you are looking for something like this: We cut your vector graphic in a PVC film, remove the uneeded part and apply a transfer tape so you can easy install it on your own."
                      )}
                    </p>
                    <Row>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "CUTTINGPLOTT",
                          context.t("CUTTINGPLOTT.title"),
                          context.t("CUTTINGPLOTT.description")
                        )}
                      </Col>
                      {/* <Col sm={smSize}>
                  {renderModalCard('MULTICUTTINGPLOTT', context.t('MULTICUTTINGPLOTT.title'), context.t('MULTICUTTINGPLOTT.description'))}
                </Col> */}
                      <Col
                        sm={smSize}
                        className="productselectionmodal__texteditorlink h-100 py-2"
                      >
                        <div>
                          <Link to={`/${language}/klebeschriften/`}>
                            <Row>
                              <Col xs={3}>
                                <img
                                  src="https://cdn.myfolie.de/media/products/klebeschriften.png"
                                  alt="selected-editor-img"
                                  className="img-fluid"
                                  loading="lazy"
                                />
                              </Col>
                              <Col xs={9}>
                                <p className="h6">
                                  {context.t("Vinyl Lettering")}
                                </p>
                                <p className="semi-small">
                                  {context.t(
                                    "If you do not have a file to upload, you should checkout or Vinyl Lettering tool where you can easily create letterings online."
                                  )}
                                </p>
                              </Col>
                            </Row>
                          </Link>
                        </div>
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="specials">
                    <Row>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "ROLLUP",
                          context.t("ROLLUP.title"),
                          context.t("ROLLUP.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "SEALLABEL",
                          context.t("SEALLABEL.title"),
                          context.t("SEALLABEL.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "STICKER_CARAVAN",
                          context.t("STICKER_CARAVAN.title"),
                          context.t("STICKER_CARAVAN.description")
                        )}
                      </Col>
                      <Col sm={smSize}>
                        {renderModalCard(
                          "STICKER_STRONG",
                          context.t("STICKER_STRONG.title"),
                          context.t("STICKER_STRONG.description")
                        )}
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </ModalBody>
            </Modal>
          </>
        )}
      />
    </EditorFormSection>
  );
};

ProductSelectionSection.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  togglePanel: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
};

ProductSelectionSection.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default ProductSelectionSection;
